import React, { useEffect, useRef, useState } from 'react'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as Sparkling } from '../../../assets/images/sparkling.svg'

const MAX_CHAT_MESSAGE_LENGTH = 200

interface AgentChatInputProps {
  handleSubmit: (chatMessage: string) => Promise<void>
  isLoading: boolean
  disabled?: boolean
}

export const AgentChatInput: React.FC<AgentChatInputProps> = ({ handleSubmit, isLoading, disabled }) => {
  const textRef = useRef<HTMLTextAreaElement>(null)

  const [chatMessage, setChatMessage] = useState<string>('')
  const [wasTruncated, setWasTruncated] = useState<boolean>(false)

  useEffect(() => {
    if (!textRef.current) {
      return
    }
    textRef.current.style.height = '0px'
    const newHeight = Math.min(textRef.current.scrollHeight, 128)
    textRef.current.style.height = newHeight + 'px'
  }, [textRef, chatMessage])

  const remainingCharacters = MAX_CHAT_MESSAGE_LENGTH - chatMessage.trim().length
  const isAtLimit = remainingCharacters === 0
  const isNearLimit = remainingCharacters < 20 && remainingCharacters > 0

  return (
    <div>
      <div className='d-flex justify-content-between pb-2'>
        <div className='d-flex align-items-end paragraph-extra-small zocks-text-warning-text min-height-2'>
          {wasTruncated ? <i className='bi bi-exclamation-triangle'></i> : null}
          {wasTruncated ? <span className='ms-2'>Input truncated to limit</span> : null}
        </div>
        <div
          className={classNames('d-flex align-items-end paragraph-extra-small px-2 min-height-2', {
            'zocks-text-gray-600': !isNearLimit && !isAtLimit,
            'zocks-text-warning-text': isNearLimit,
            'zocks-text-danger-200': isAtLimit,
          })}
        >
          {chatMessage.trim().length > 0 ? (isAtLimit ? 'Limit reached' : `${remainingCharacters} characters left`) : null}
        </div>
      </div>

      <ScopedBlock
        scope={{
          scope: 'ai_agent:interact',
          superScope: ['ai_agent:admin'],
        }}
        quota={{
          quota: 'ai.agent.value',
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <div className='d-flex p-2 align-items-center ai-agent-chat-textarea-container'>
          <div className='d-flex flex-grow-1 gap-3 zocks-px-0.75'>
            <div className='d-flex gap-2 align-items-center flex-grow-1'>
              <Sparkling
                className={classNames({
                  'zocks-text-gray-900': chatMessage.trim() && !isLoading,
                  'zocks-text-gray-500': !chatMessage.trim() || isLoading,
                })}
              />
              <Form.Control
                ref={textRef}
                className='px-0 border-0'
                as='textarea'
                value={chatMessage}
                maxLength={MAX_CHAT_MESSAGE_LENGTH}
                onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
                  const { selectionStart = 0, selectionEnd = 0 } = e.currentTarget
                  const pastedLength = e.clipboardData.getData('text').length
                  const resultingLength = chatMessage.length - (selectionEnd - selectionStart) + pastedLength

                  if (resultingLength > MAX_CHAT_MESSAGE_LENGTH) {
                    setWasTruncated(true)
                  }
                }}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const newValue = e.currentTarget.value
                  setWasTruncated(wasTruncated && newValue.length >= MAX_CHAT_MESSAGE_LENGTH)
                  setChatMessage(newValue)
                }}
                disabled={disabled}
                onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (isLoading || disabled) {
                    return
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (e.ctrlKey || e.metaKey) {
                      setChatMessage((chatMessage) => `${chatMessage}\n`)
                    } else if (chatMessage.trim().length >= 3 && chatMessage.trim().length <= MAX_CHAT_MESSAGE_LENGTH) {
                      setChatMessage('')
                      await handleSubmit(chatMessage)
                    }
                  }
                }}
                placeholder='Ask anything…'
              />
            </div>
          </div>
          <div className='d-flex gap-1 justify-content-end align-items-center'>
            <Button
              variant='secondary'
              className='button-medium px-1 py-2 border-0 bg-white'
              onClick={async () => {
                setChatMessage('')
                await handleSubmit(chatMessage)
                textRef.current?.focus()
              }}
              loading={isLoading}
              disabled={disabled || chatMessage.trim().length < 3 || chatMessage.trim().length > MAX_CHAT_MESSAGE_LENGTH}
            >
              <i className='bi bi-arrow-right-circle-fill fs-4' />
            </Button>
          </div>
        </div>
      </ScopedBlock>
    </div>
  )
}
